import React from "react";
import {
  Avatar,
  Box,
  Button,
  Grid,
  Hidden,
  LinearProgress,
  Link,
  Stack,
  Typography,
} from "@mui/material";
import { LinkToShare } from "../apps-components/apps-types";
import {
  Android,
  Apple,
  InstallDesktop,
  InstallMobile,
} from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import QRCode from "react-qr-code";
import WrongMobilePlatform from "../../pages/WrongPlatform";
import api from "../../pages/api/base-url";
import DownloadProgressCircle from "./DownloadProgressCircle";

function VersionAndButton(props: {
  link: LinkToShare;
  linkText: string;
  extension: string;
  platform: string;
  os: string;
  progress: number;
  appName?: string;
  linkForManifest?: string;
  downloadProgress: number;
  setDownloadProgress: any;
}) {
  const { t } = useTranslation();

  function handleDownload(linkText: string, extension: string, name?: string) {
    api
      .get(linkText, {
        responseType: "blob",
        onDownloadProgress: (progressEvent) => {
          props.setDownloadProgress(
            Math.round((progressEvent.loaded * 100) / progressEvent.total)
          );
        },
      })
      .then((response) => {
        const link = document.createElement("a");
        const url = window.URL.createObjectURL(new Blob([response.data]));
        link.href = url;

        if (name) {
          link.setAttribute("download", name + extension);
        } else {
          link.setAttribute("download", "application-name" + extension);
        }
        document.body.appendChild(link);
        link.click();
      });
  }

  return (
    <Box sx={{ width: "100%" }}>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        spacing={props.os === "Other" ? 10 : 4}
      >
        <Stack
          direction="row"
          justifyContent="center"
          alignItems="flex-end"
          spacing={1}
        >
          {props.platform === "IOS" ? (
            <Apple color="action" sx={{ width: 35, height: 35 }} />
          ) : (
            <Android color="action" sx={{ width: 35, height: 35 }} />
          )}
          <Typography variant="h6" sx={{ color: "text.secondary" }}>
            {
              props.link.versions.filter(
                (item) => item.platform === props.platform
              )[0].versionNumber
            }
          </Typography>
        </Stack>
        {props.os !== "iOS" && (
          <Button
            variant="contained"
            onClick={() =>
              handleDownload(props.linkText, props.extension, props.appName)
            }
            startIcon={
              props.os === "Other" ? <InstallDesktop /> : <InstallMobile />
            }
          >
            {"Download"}
          </Button>
        )}

        {props.platform === "IOS" && props.os === "iOS" && (
          <Link
            href={props.linkForManifest}
            target="_top"
            variant="subtitle1"
            underline="none"
            sx={{
              color: "white",
              backgroundColor: "#58342C",
              borderRadius: 1,
              boxShadow: 1,
              padding: "7px 25px",
              "&:hover": {
                color: "#FFFFFF",
                backgroundColor: "#402218",
              },
            }}
          >
            <Stack direction="row" spacing={1}>
              <InstallMobile sx={{ fontSize: 20 }} />
              <Typography variant="subtitle2">{t("Download")}</Typography>
            </Stack>
          </Link>
        )}
      </Stack>
    </Box>
  );
}

export function QRCodeSection(props: { link: string }) {
  return (
    <Hidden smDown>
      <Grid item container alignItems="center" justifyContent="center" xs={6}>
        <Grid
          item
          container
          direction="column"
          alignItems="center"
          justifyContent="center"
          padding={2}
          spacing={2}
        >
          <Grid item>
            <QRCode
              size={200}
              style={{ height: "auto", maxWidth: "100%", width: "100%" }}
              value={props.link}
              viewBox={`0 0 200 200`}
            />
          </Grid>
        </Grid>
      </Grid>
    </Hidden>
  );
}

export default function DownloadElements(props: {
  platform: string;
  os: string;
  link: LinkToShare;
  progress: number;
  linkTextAndroid: string;
  linkTextIOS: string;
  urlForManifest?: string;
}) {
  const { t } = useTranslation();
  const [downloadProgress, setDownloadProgress] = React.useState(0);

  return (props.os === "Android" && props.platform.includes("Android")) ||
    (props.os === "iOS" && props.platform.includes("iOS")) ||
    props.os === "Other" ? (
    <Box sx={{ display: "flex", justifyContent: "center", height: "80vh" }}>
      <Stack
        direction="column"
        justifyContent="space-around"
        alignItems="center"
        spacing={2}
      >
        <Stack justifyContent="flex-start" alignItems="center" spacing={2}>
          <Avatar
            alt={props.link.appName}
            src={`${process.env.REACT_APP_BACKEND_BASE_URL}apps/${props.link.appId}/icon`}
            sx={{ width: 100, height: 100, borderRadius: 1.5 }}
          />
          <Typography variant="h2">{props.link.appName}</Typography>
        </Stack>
        <Box>
          <Stack
            direction="column"
            alignItems="center"
            justifyContent="center"
            spacing={2}
          >
            <Typography textAlign="center" variant="h5" sx={{ mb: 1 }}>
              {props.link.versions.length > 1
                ? t("App versions") + ": "
                : t("App version") + ": "}
            </Typography>
            {(props.platform === "Android; iOS;" ||
              props.platform === "Android") &&
              (props.os === "Other" || props.os === "Android") && (
                <VersionAndButton
                  appName={props.link.appName}
                  link={props.link}
                  platform={"ANDROID"}
                  os={props.os}
                  linkText={props.linkTextAndroid}
                  extension={".apk"}
                  progress={props.progress}
                  downloadProgress={downloadProgress}
                  setDownloadProgress={setDownloadProgress}
                />
              )}
            {(props.platform === "Android; iOS;" || props.platform === "iOS") &&
              (props.os === "Other" || props.os === "iOS") && (
                <VersionAndButton
                  appName={props.link.appName}
                  link={props.link}
                  platform={"IOS"}
                  os={props.os}
                  linkText={props.linkTextIOS}
                  extension={".ipa"}
                  linkForManifest={props.urlForManifest}
                  progress={props.progress}
                  downloadProgress={downloadProgress}
                  setDownloadProgress={setDownloadProgress}
                />
              )}
            <Box sx={{ py: 1 }}>
              {downloadProgress > 0 && props.os !== "iOS" && (
                <DownloadProgressCircle progress={downloadProgress} />
              )}
            </Box>
          </Stack>
        </Box>
        <Stack justifyContent="flex-end" alignItems="center" spacing={2}>
          <Stack
            direction="row"
            justifyContent="center"
            alignItems="center"
            spacing={1}
          >
            <Typography variant="h5" textAlign="center">
              {t("Expiring at: ")}
            </Typography>
            <Typography variant="body1" textAlign="center">
              {new Date(props.link.validity_end).toLocaleString()}
            </Typography>
          </Stack>
          <LinearProgress
            variant="determinate"
            value={props.progress}
            color="primary"
            sx={{ width: 250, height: 20, borderRadius: 10 }}
          />
        </Stack>
      </Stack>
    </Box>
  ) : (
    <WrongMobilePlatform platform={props.platform} />
  );
}
